import { useState } from 'react';
import { Bell, FileText, ExternalLink, Reply, FileQuestion, ListTodo, CheckSquare, MessageSquare, Vote } from 'lucide-react';
import Markdown from 'react-markdown';
import remarkGfm from 'remark-gfm';
import remarkMath from 'remark-math';
import rehypeKatex from 'rehype-katex';
import type { IacpEvent } from '../api/types';
import './IacpEventCard.css';
import { serviceOrigin } from '../lib/services';

interface Props {
  event: IacpEvent;
  sender?: string;
}

const REMARK_PLUGINS = [remarkGfm, remarkMath];
const REHYPE_PLUGINS = [rehypeKatex];

function formatRelative(iso: string): string {
  const t = Date.parse(iso);
  if (!Number.isFinite(t)) return '';
  const diff = (Date.now() - t) / 1000;
  if (diff < 45) return 'jetzt';
  if (diff < 3600) return `vor ${Math.floor(diff / 60)} Min`;
  if (diff < 86_400) return `vor ${Math.floor(diff / 3600)}h`;
  const days = Math.floor(diff / 86_400);
  if (days === 1) return 'gestern';
  if (days < 7) return `vor ${days} Tagen`;
  return new Date(t).toLocaleDateString('de-DE');
}

// event.author_id is "prefix:name" — split and colour the dot per prefix.
// Unknown prefix → plain name + grey dot, no crash.
function parseAuthor(authorId: string): { name: string; prefix: string } {
  const idx = authorId.indexOf(':');
  if (idx < 0) return { name: authorId, prefix: '' };
  return { prefix: authorId.slice(0, idx), name: authorId.slice(idx + 1) };
}

function eventIcon(event: string) {
  if (event === 'chunk_committed') return <FileText size={14} />;
  return <Bell size={14} />;
}

// Tolerant XML-attribute parser. Server-side `_REPLY_OPEN_RE` matches
// `<tag attr="val" attr="val">` shape; we do the same to extract attrs
// without pulling in a full DOM parser. Truncated tail (`</reply>`
// missing because text preview was cut at ~600 chars) is fine — we
// only need the opener attrs and the body before the close.
function parseTaggedBlock(
  text: string | undefined | null,
  tag: string,
): { attrs: Record<string, string>; body: string } | null {
  if (!text) return null;
  const openMatch = new RegExp(`^\\s*<${tag}\\b([^>]*)>`, 's').exec(text);
  if (!openMatch) return null;
  const attrs: Record<string, string> = {};
  const attrRe = /(\w[\w-]*)="([^"]*)"/g;
  let am: RegExpExecArray | null;
  while ((am = attrRe.exec(openMatch[1])) !== null) {
    attrs[am[1]] = am[2];
  }
  let body = text.slice(openMatch[0].length);
  const closeMatch = new RegExp(`</${tag}>\\s*$`, 's').exec(body);
  if (closeMatch) body = body.slice(0, closeMatch.index);
  return { attrs, body: stripSectionEnvelope(body) };
}

// The section CONTENT is wrapped in `<main>…</main>` (the Y.Doc paragraph form);
// some posts even carry a doubled `<main> <main>…</main> </main>` wrapper. These
// structural tags are never literal content — strip them so the body renders as
// text instead of leaking raw `<main>` into the card (Alex 2026-06-29). Tolerant:
// removes every <main>/<section> opener/closer regardless of nesting/malformation.
function stripSectionEnvelope(body: string): string {
  return body
    .replace(/<\/?main\b[^>]*>/gi, '')
    .replace(/<\/?section\b[^>]*>/gi, '')
    .trim();
}

// Attribute, die bei einer Kopf-Änderung wirklich etwas aussagen. Bewusst
// kurz gehalten: name/goal/language ändern sich praktisch nie, Status und
// Freigaben sind der Grund, warum der Push überhaupt kommt.
const ATTR_LABELS: Array<[string, string]> = [
  ['status', 'Status'],
  ['approvals', 'Freigaben'],
  ['owner', 'Eigentümer'],
  ['editors', 'Editoren'],
  ['last_revised_by', 'zuletzt geändert von'],
];

/** Listenwerte kürzen: „a,b,c,d" wird zu „4 · a, b …" — die Anzahl ist die
 *  Information, die man beim Überfliegen braucht, die Namen stehen im Tooltip. */
function shortAttr(v: string): string {
  const parts = v.split(',').map((x) => x.trim()).filter(Boolean);
  if (parts.length <= 2) return v;
  return `${parts.length} · ${parts.slice(0, 2).join(', ')} …`;
}

const SECTION_TYPE_LABEL: Record<string, string> = {
  product: 'Product',
  question: 'Question',
  task: 'Task',
  annotation: 'Note',
  vote: 'Vote',
};

const SECTION_TYPE_ICON: Record<string, React.ReactNode> = {
  product: <CheckSquare size={12} />,
  question: <FileQuestion size={12} />,
  task: <ListTodo size={12} />,
  annotation: <MessageSquare size={12} />,
  vote: <Vote size={12} />,
};

// Truncation indicator — `text` is capped at ~600 chars server-side and
// long replies end with `…` instead of the close tag. Surfacing this so
// the user knows to open the post for the full body.
function isTruncated(text: string | undefined | null): boolean {
  return !!text && text.trim().endsWith('…');
}

export function IacpEventCard({ event, sender }: Props) {
  const [expanded, setExpanded] = useState(false);
  const author = parseAuthor(event.author_id);
  const requiresResponse = event.requires_response === true;
  // Registry-Ursprung + EINZAHL-Pfad: die Beitrags-Oberfläche kennt nur
  // `/post/:id`; `/posts/…` lud die Hülle ohne passende Route (18.08. gemessen).
  const openUrl = `${serviceOrigin('content_web') || 'https://content.arkturian.com'}/post/${event.post_id}`;

  // section_role drives the body render — Content's IACP f8203b07
  // explicitly says route by role, NOT by tag-sniffing. The XML in
  // `text` is just the raw Y.Doc paragraph form.
  const role = event.section_role;
  const reply = role === 'reply' ? parseTaggedBlock(event.text, 'reply') : null;
  const sectionOpener = role === 'start' ? parseTaggedBlock(event.text, 'section') : null;
  const truncated = isTruncated(event.text);

  // For role=end we render nothing of substance — section close is a
  // boundary marker, not content. Content recommended a no-op render.
  if (role === 'end') {
    return (
      <div className="iacp-event-card iacp-event-card-end">
        <span className="iacp-section-end">— Section geschlossen —</span>
      </div>
    );
  }

  const sectionTypeBadge = event.section_type && (
    <span className={`iacp-section-type type-${event.section_type}`}>
      {SECTION_TYPE_ICON[event.section_type]}
      {SECTION_TYPE_LABEL[event.section_type] || event.section_type}
    </span>
  );

  const targetsLine = event.targets && event.targets.length > 0 && (
    <div className="iacp-targets-row">
      {event.primary_recipient && (
        <span className="iacp-target-chip primary" title="Primary recipient">
          →{event.primary_recipient}
        </span>
      )}
      {event.targets
        .filter((t) => t !== event.primary_recipient)
        .map((t) => (
          <span key={t} className="iacp-target-chip" title="CC recipient">
            cc:{t}
          </span>
        ))}
    </div>
  );

  return (
    <div
      className={`iacp-event-card${requiresResponse ? ' requires-response' : ''} role-${role || 'plain'}`}
    >
      <div className="iacp-event-header">
        <span className="iacp-event-icon">{eventIcon(event.event)}</span>
        <span className="iacp-event-sender">{sender || 'IACP'}</span>
        <span className="iacp-event-badge">IACP</span>
        {sectionTypeBadge}
        {role && role !== 'content' && (
          <span className={`iacp-section-role role-${role}`}>{role}</span>
        )}
        {event.your_role && (
          <span className="iacp-your-role" title="Your role in this product">
            {event.your_role}
          </span>
        )}
        <span className="iacp-event-spacer" />
        {requiresResponse && (
          <span className="iacp-event-needs-response">Antwort erwartet</span>
        )}
        <span className="iacp-event-time" title={event.ts}>
          {formatRelative(event.ts)}
        </span>
      </div>

      <div className="iacp-event-meta">
        <span className={`iacp-author-chip prefix-${author.prefix || 'none'}`}>
          <span className="iacp-author-dot" />
          {author.name}
        </span>
        <span className={`iacp-trigger-badge trigger-${event.trigger || 'unknown'}`}>
          {event.trigger || 'unknown'}
        </span>
      </div>

      {targetsLine}

      {/* Body render switches on section_role */}
      {reply ? (
        <div className="iacp-reply-block">
          <div className="iacp-reply-head">
            <Reply size={11} className="iacp-reply-icon" />
            <span className="iacp-reply-by">{reply.attrs.by || author.name}</span>
            {reply.attrs.at && (
              <span className="iacp-reply-at" title={reply.attrs.at}>
                {formatRelative(reply.attrs.at)}
              </span>
            )}
          </div>
          <div className={`iacp-reply-body${expanded ? ' expanded' : ''}`}>
            <Markdown remarkPlugins={REMARK_PLUGINS} rehypePlugins={REHYPE_PLUGINS}>
              {reply.body}
            </Markdown>
          </div>
        </div>
      ) : sectionOpener ? (
        <div className="iacp-section-opener">
          <div className="iacp-section-opener-head">
            {sectionOpener.attrs.type && (
              <span className={`iacp-section-type type-${sectionOpener.attrs.type}`}>
                {SECTION_TYPE_ICON[sectionOpener.attrs.type]}
                {SECTION_TYPE_LABEL[sectionOpener.attrs.type] || sectionOpener.attrs.type}
              </span>
            )}
            {sectionOpener.attrs.id && (
              <span className="iacp-section-id" title={sectionOpener.attrs.id}>
                #{sectionOpener.attrs.id.slice(0, 8)}
              </span>
            )}
            {sectionOpener.attrs.status && (
              <span className={`iacp-section-status status-${sectionOpener.attrs.status}`}>
                {sectionOpener.attrs.status}
              </span>
            )}
            {sectionOpener.attrs.initiator && (
              <span className="iacp-section-initiator">
                von {sectionOpener.attrs.initiator}
              </span>
            )}
          </div>
          {sectionOpener.body ? (
            <div className={`iacp-event-body iacp-event-body-md${expanded ? ' expanded' : ''}`}>
              <Markdown remarkPlugins={REMARK_PLUGINS} rehypePlugins={REHYPE_PLUGINS}>
                {sectionOpener.body}
              </Markdown>
            </div>
          ) : (
            // Ein Section-Kopf OHNE Körper ist kein leeres Ereignis, sondern eine
            // Änderung an den Attributen: Status, Freigaben, Eigentum, Editoren.
            // Genau so laufen Abnahmen — jede Reviewer-Stimme erzeugt so einen
            // Push. Bisher stand hier eine Karte mit Marken und sonst nichts, und
            // Alex fragte zu Recht, was das soll (2026-08-07). Jetzt steht da,
            // WAS sich geändert hat.
            <div className="iacp-attr-change">
              <span className="iacp-attr-change-lead">Kopf aktualisiert</span>
              {ATTR_LABELS.filter(([k]) => sectionOpener.attrs[k]).map(([k, label]) => (
                <span key={k} className="iacp-attr-chip" title={sectionOpener.attrs[k]}>
                  {label}: <strong>{shortAttr(sectionOpener.attrs[k])}</strong>
                </span>
              ))}
            </div>
          )}
        </div>
      ) : (
        // role='content' or no role — plain markdown body. Same render
        // path; difference is purely contextual (inside vs outside a
        // section), which is already conveyed by the section badge.
        <div className={`iacp-event-body iacp-event-body-md${expanded ? ' expanded' : ''}`}>
          <Markdown remarkPlugins={REMARK_PLUGINS} rehypePlugins={REHYPE_PLUGINS}>
            {stripSectionEnvelope(event.text || '')}
          </Markdown>
        </div>
      )}

      {!expanded && ((event.text?.length ?? 0) > 220 || truncated) && (
        <button
          type="button"
          className="iacp-event-more"
          onClick={() => setExpanded(true)}
        >
          {truncated ? 'Im Post lesen (gekürzt)' : 'Mehr anzeigen'}
        </button>
      )}

      <div className="iacp-event-footer">
        <span className="iacp-event-postref">
          Post #{event.post_id} · v{event.doc_version}
          {event.section_id && ` · §${event.section_id.slice(0, 8)}`}
        </span>
        <a
          href={openUrl}
          target="_blank"
          rel="noopener noreferrer"
          className="iacp-event-open"
        >
          <ExternalLink size={11} />
          Open
        </a>
      </div>
    </div>
  );
}
